import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import {
  lockScreenOrientation,
  unlockScreenOrientation,
} from "@src/appV2/NativeAdapters/screenOrientation.adapter";
import { DOCUMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";

import { Button } from "../../components/Button";
import { CourseResultsBottomSheet } from "./CourseResultsBottomSheet";
import { ScormPlayerHeader } from "./ScormPlayerHeader";
import { buildContentUrl, type LessonStatus, parseLearnerName } from "./scormUtils";
import { useCourseEnrollmentLifecycle } from "./useCourseEnrollmentLifecycle";
import { type ScormApiRef, useScormApi } from "./useScormApi";
import { useGetScormCourseConfig } from "./useScormCourseConfig";

const RESULTS_DELAY_MS = 3000;
const COURSE_LOADING_FALLBACK_MS = 15_000;
const SUPPORTED_SCORM_VERSION = "1.2";

export function ScormPlayerPage(): ReactElement {
  const { requirementId } = useParams<{ requirementId: string }>();
  const history = useHistory();
  const worker = useDefinedWorker();
  const [lessonStatus, setLessonStatus] = useState<LessonStatus>("In Progress");
  const [isCourseLoading, setIsCourseLoading] = useState(true);
  const resultsModalState = useModalState();
  const resultsShownRef = useRef(false);
  const historyLengthOnMountRef = useRef(window.history.length);
  const scormApiRef = useRef<ScormApiRef>();

  const { getScormConfig } = useGetScormCourseConfig();
  const config = getScormConfig(requirementId);
  const isRusticiDispatch = isDefined(config) && config.type === "rusticiDispatch";
  const isSupportedScormVersion =
    isRusticiDispatch && config.scormVersion === SUPPORTED_SCORM_VERSION;

  useEffect(() => {
    if (isRusticiDispatch && !isSupportedScormVersion) {
      logError(APP_V2_APP_EVENTS.SCORM_UNSUPPORTED_VERSION, {
        error: new Error(
          `Unsupported SCORM version: ${config.scormVersion}. Only 1.2 is supported.`
        ),
        metadata: { requirementId, courseId: config.courseId },
      });
    }
  }, [config, isRusticiDispatch, isSupportedScormVersion, requirementId]);

  const { firstName: learnerFirstName, lastName: learnerLastName } = useMemo(
    () =>
      parseLearnerName({
        name: worker.name,
        firstName: worker.firstName,
        lastName: worker.lastName,
      }),
    [worker.firstName, worker.lastName, worker.name]
  );

  const handleCourseLoaded = useCallback((): void => {
    setIsCourseLoading(false);
  }, []);

  const { courseEnrollmentId } = useCourseEnrollmentLifecycle({
    courseId: isRusticiDispatch ? config.courseId : "",
    courseName: isRusticiDispatch ? config.courseName : "",
    requirementId,
    enabled: isSupportedScormVersion,
    lessonStatus,
    scormApiRef,
  });

  useScormApi({
    scormApiRef,
    courseId: isRusticiDispatch ? config.courseId : "",
    requirementId,
    learnerId: courseEnrollmentId ?? "",
    learnerFirstName,
    learnerLastName,
    enabled: isSupportedScormVersion && isDefined(courseEnrollmentId),
    onStatusChange: setLessonStatus,
    onCourseLoaded: handleCourseLoaded,
  });

  useEffect(() => {
    if (!isCourseLoading) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setIsCourseLoading(false);
    }, COURSE_LOADING_FALLBACK_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isCourseLoading]);

  useEffect(() => {
    if (resultsModalState.modalIsOpen) {
      void lockScreenOrientation("portrait-primary");
    } else {
      void unlockScreenOrientation();
    }
    return () => {
      void unlockScreenOrientation();
    };
  }, [resultsModalState.modalIsOpen]);

  useEffect(() => {
    if (lessonStatus !== "Completed" || resultsShownRef.current) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      resultsShownRef.current = true;
      resultsModalState.openModal();
    }, RESULTS_DELAY_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [lessonStatus, resultsModalState]);

  const navigateBackOrToDocuments = useCallback(() => {
    const entriesAddedSinceMount = window.history.length - historyLengthOnMountRef.current;
    const stepsBack = entriesAddedSinceMount + 1;
    if (stepsBack > 0 && window.history.length > stepsBack) {
      history.go(-stepsBack);
      return;
    }
    const target = generatePath(DOCUMENT_DETAILS_FULL_PATH, { requirementId });
    history.replace(target);
  }, [history, requirementId]);

  const courseUrl = useMemo(() => {
    if (!isSupportedScormVersion || !isDefined(courseEnrollmentId)) {
      return "";
    }
    const resolvedContentUrl = buildContentUrl({
      contentUrlTemplate: config.contentUrl,
      dispatchRoot: config.dispatchRoot,
      learnerId: courseEnrollmentId,
      learnerFirstName,
      learnerLastName,
    });
    const wrapperParams = new URLSearchParams({
      dispatchRoot: config.dispatchRoot,
      contentUrl: resolvedContentUrl,
    });
    return `/scorm/dispatch-wrapper.html?${wrapperParams.toString()}`;
  }, [config, courseEnrollmentId, isSupportedScormVersion, learnerFirstName, learnerLastName]);

  if (!isDefined(config)) {
    return (
      <ThemeProvider theme={getTheme()}>
        <Stack sx={{ padding: 4, gap: 2 }}>
          <Text>No course configuration found for this requirement.</Text>
          <Button variant="contained" onClick={navigateBackOrToDocuments}>
            Back to Documents
          </Button>
        </Stack>
      </ThemeProvider>
    );
  }

  if (isRusticiDispatch && !isSupportedScormVersion) {
    return (
      <ThemeProvider theme={getTheme()}>
        <Stack sx={{ padding: 4, gap: 2 }}>
          <Text>This course is not supported on your app version.</Text>
          <Button variant="contained" onClick={navigateBackOrToDocuments}>
            Back to Documents
          </Button>
        </Stack>
      </ThemeProvider>
    );
  }

  const showLoadFailure = !isCourseLoading && courseUrl === "";

  return (
    <ThemeProvider theme={getTheme()}>
      <Box
        sx={(theme) => ({
          height: "100dvh",
          width: "100%",
          background: theme.palette.background.default,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        })}
      >
        <ScormPlayerHeader courseName={config.courseName} onBack={navigateBackOrToDocuments} />
        {isCourseLoading && (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        )}
        {showLoadFailure && (
          <Stack
            sx={{ flex: 1, alignItems: "center", justifyContent: "center", gap: 2, padding: 4 }}
          >
            <Text>We couldn&apos;t load this course. Please try again.</Text>
            <Button variant="contained" onClick={navigateBackOrToDocuments}>
              Back to Documents
            </Button>
          </Stack>
        )}
        {courseUrl !== "" && (
          <Box
            component="iframe"
            src={courseUrl}
            title={config.courseName}
            scrolling="no"
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              border: "none",
              display: isCourseLoading ? "none" : "block",
              margin: 0,
              padding: 0,
            }}
            allow="fullscreen"
          />
        )}
        <CourseResultsBottomSheet
          modalState={resultsModalState}
          courseName={config.courseName}
          onBackToDocuments={() => {
            resultsModalState.closeModal();
            navigateBackOrToDocuments();
          }}
        />
      </Box>
    </ThemeProvider>
  );
}
